/**
 * Shapes for every endpoint the admin UI talks to. Mirrors
 * `apps/app/src/routes/{auth,campaigns,sending}.ts` for existing
 * endpoints and `docs/api-ui.md` for the new ones (health-summary,
 * stats, contacts, brand-kit, preflight). Kept hand-written (no codegen)
 * since the backend has no OpenAPI source yet.
 */

import type { SectionEditModel, SectionUnmappedEdit } from '@nopain-newsletter/core/sections';
import type { CatalogProduct } from '@nopain-newsletter/core/blocks';

export type Role = 'admin' | 'editor' | 'viewer';

/**
 * Stored section edits of an HTML template or campaign (`sections` on a
 * single read): the original the slot and section ids are computed from,
 * and the edit model. `html` is then the render of the two. `null` when
 * there are none: the current `html` is the original.
 */
export interface StoredSectionEdits {
  original: string;
  model: SectionEditModel;
}

/** Answer of `PUT /api/templates/:id/sections` and `PUT /api/campaigns/:id/sections`. */
export interface SectionSaveResponse {
  template?: Template;
  campaign?: Campaign;
  /** Edits the server could not apply to the stored original. */
  unmapped: SectionUnmappedEdit[];
}

export interface MeResponse {
  userId: string;
  email?: string | null;
  tenantId: string | null;
  role: Role | null;
  totpEnabled: boolean | null;
  /** The user's own name for the admin ("Change name"); null = guessed from the email. */
  displayName?: string | null;
  csrfToken: string;
  /** End of the 30-minute step-up window (epoch ms), or null. In the future = no code needed. */
  stepUpUntil: number | null;
  /** What the member's custom role takes away (K8). Missing or empty = nothing. */
  deniedCapabilities?: Capability[];
}

/** K8 custom roles: the fixed list of things a custom role can take away. */
export type Capability =
  | 'campaigns.edit'
  | 'campaigns.test_send'
  | 'campaigns.send'
  | 'templates.edit'
  | 'contacts.edit'
  | 'forms.edit'
  | 'ai.use'
  | 'settings.edit';

/** One custom role: a base role (never admin) minus `denied`. */
export interface CustomRole {
  id: string;
  name: string;
  baseRole: 'editor' | 'viewer';
  denied: Capability[];
  memberCount: number;
  createdAt: number;
  updatedAt: number;
}

/** `GET /api/custom-roles` (admin-only). */
export interface CustomRolesResponse {
  roles: CustomRole[];
  capabilities: Capability[];
  bases: Array<'editor' | 'viewer'>;
  maxRoles: number;
}

export type LoginResponse = { status: 'totp_required' } | MeResponse;

/**
 * Exactly the statuses the API returns (`campaigns.status`). There is no
 * "pending approval" status: a campaign waiting for an admin is a `draft`
 * with a test email sent (`isAwaitingApproval`, CampaignStatusBadge.tsx).
 * `scheduled` = approved, waiting for its send time (or the next minute).
 */
export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'paused' | 'sent' | 'failed';

export interface Campaign {
  id: string;
  senderId: string;
  templateId: string | null;
  name: string;
  subject: string;
  /** Preview text an inbox shows after the subject; `null` = none (the inbox falls back to the first line). */
  preheader: string | null;
  html: string;
  /**
   * The block editor's document, stored opaquely (`{ blocks: Block[] }`,
   * see `lib/blocks.ts#blocksFromJson`). `null` for campaigns saved before
   * the API stored it — the editor shows those read-only until "Start over".
   */
  blockJson: unknown;
  status: CampaignStatus;
  /** `flow_step`: made by an automation (never a draft, no approval per send); everything else is `campaign`. */
  kind: 'campaign' | 'flow_step';
  scheduledAt: number | null;
  createdBy: string;
  approvedBy: string | null;
  approvedAt: number | null;
  testSendDoneAt: number | null;
  createdAt: number;
  sentAt: number | null;
  /** K8: organising tags (not audience rules); present on list reads. */
  tags?: string[];
  /** Section editor state; only on single-campaign reads and writes. */
  sections?: StoredSectionEdits | null;
  /** Who receives it (docs/api-ui.md "Målgruppe"). Present on single-campaign reads and writes. */
  audience?: CampaignAudience;
  /** Estimated recipients after suppressions and consent (frequency cap not applied). */
  audienceEstimate?: AudienceEstimate;
  /** Fase 6 A/B-emnelinjetest. */
  abEnabled: boolean;
  abSplitPercent: number;
  abWaitHours: number;
  subjectB: string | null;
  /** K6: content B (HTML) of an A/B test; `null` = both groups get the same content. */
  htmlB?: string | null;
  /** K6: the winner goes to the rest automatically only when true (off for new tests). */
  abAutoSend?: boolean;
  abMetric?: 'clicks' | 'opens';
  /** K6: 'local_time' sends at the same wall-clock time in each recipient's zone; 'smart' = each contact at their usual hour inside a 24 h window from scheduledAt (opt-in). */
  sendMode?: 'fixed' | 'local_time' | 'smart';
  /** YYYY-MM-DD, local-time mode only. */
  localSendDate?: string | null;
  /** Minutes after midnight (0..1439), local-time mode only. */
  localSendMinutes?: number | null;
  /** IANA zone for recipients without a known country; null = the API default (Europe/Berlin). */
  sendFallbackTz?: string | null;
  abTestStartedAt: number | null;
  abWinnerVariant: 'a' | 'b' | null;
  abWinnerDecidedAt: number | null;
  abNoSignificantWinner: boolean;
  /** Fase 6 "Gensend til ikke-klikkere": non-null when this campaign IS a resend of another. */
  resendOfCampaignId: string | null;
  resendKind: 'non_clickers' | null;
  /**
   * Personal discount code (migration 0054): each recipient gets their own
   * single-use code, locked to their address and made at send time. Null = off.
   */
  coupon?: CampaignCoupon | null;
  /** Hidden from the campaign list since then (migration 0055). Null = listed; only sent or failed campaigns can be archived. */
  archivedAt?: number | null;
}

export interface CampaignCoupon {
  /** 1-50. */
  percent: number;
  /** 1-30. */
  validDays: number;
  /** 2-8 characters A-Z/0-9; codes look like `NP-7KQ4M2XC`. */
  prefix: string;
}

// --- Lister, afsendere og målgruppe (docs/api-ui.md) ---

/**
 * Lists are a union; labels and segments narrow it: every `include*`
 * must match, none of the `exclude*` may.
 */
export interface CampaignAudience {
  listIds: string[];
  includeLabels: string[];
  excludeLabels: string[];
  includeSegmentIds: string[];
  excludeSegmentIds: string[];
  /** Epoch ms: contacts with a valid order placed at or after this are left out; null = off. */
  excludeOrderedSince: number | null;
}

export interface AudienceEstimate {
  listMembers: number;
  /** Who prepare would queue if the campaign went out now (frequency cap included). */
  estimatedRecipients: number;
  /** Pass everything else, but already got the cap's 2 mails in the last 7 days. */
  heldByFrequencyCap: number;
}

/** K6 smart send time: how many recipients have a usual hour (POST /campaigns/smart-send-coverage). */
export interface SmartSendCoverage {
  recipients: number;
  withData: number;
  fallback: number;
  /** Minimum human opens/clicks before a contact's own hour is used. */
  minEvents: number;
}

export interface MailingList {
  id: string;
  name: string;
  description: string | null;
  doubleOptin: boolean;
  archived: boolean;
  archivedAt: number | null;
  /** Reachable from the public signup form's `listId` (migration 0018). */
  isPublic: boolean;
  /** The list a bare public signup (no `listId`) lands on. At most one list per tenant. */
  isDefaultSignup: boolean;
  confirmedCount: number;
  campaignCount: number;
  createdAt: number;
  updatedAt: number | null;
}

export type SenderDomainStatus = 'pending' | 'verified' | 'failed';

export interface Sender {
  id: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string | null;
  domainStatus: SenderDomainStatus | null;
  dkimVerified: boolean;
  openTrackingEnabled: boolean;
  archived: boolean;
  archivedAt: number | null;
  campaignCount: number;
  createdAt: number;
  updatedAt: number | null;
}

/** `GET/POST /api/senders/:id/warmup*` (fase-4 warm-up admin panel). */
export interface WarmupAdmin {
  /** The sender's own daily cap (`senders.daily_limit`); the curve never plans above it. */
  dailyLimit: number;
  active: boolean;
  paused: boolean;
  target: number | null;
  startedAt: number | null;
  dayOffset: number;
  limitToday: number;
  plan: { dayOffset: number; maxSends: number }[];
}

export interface SenderDomain {
  id: string;
  domain: string;
  status: SenderDomainStatus;
  verificationMethod: string | null;
  verifiedAt: number | null;
  senderCount: number;
  createdAt: number;
}

export type SegmentFieldOp = 'eq' | 'neq' | 'contains' | 'exists' | 'not_exists';

export interface SegmentFieldCondition {
  key: string;
  op: SegmentFieldOp;
  value?: string;
}

/** Mirrors `segments.repo.ts#EngagementCondition` (human clicks only; the open pixel is off). */
export interface SegmentEngagement {
  kind: 'clicked_within' | 'not_clicked_within';
  /** Whole days, 1-730. */
  days: number;
  /** Only for `not_clicked_within`: mails the contact must have received (default 3). */
  minSends?: number;
}

export type SegmentEventWindow = { days: number } | { since: string };

/** Mirrors `segmentConditions.ts#EventCondition`. `event` is a key of the server's event registry. */
export interface SegmentEventCondition {
  event: string;
  op: 'at_least' | 'at_most';
  count: number;
  window?: SegmentEventWindow;
  where?: { productId?: string; productName?: string };
}

/** Mirrors `segmentConditions.ts#PurchaseCondition`. Value in EUR, net of refunds. */
export interface SegmentPurchaseCondition {
  metric: 'total_spent' | 'average_order_value';
  op: 'at_least' | 'at_most';
  value: number;
  window?: SegmentEventWindow;
}

/**
 * K6: an estimate from order history (packages/core/src/db/tenant/predictions.ts).
 * A contact without enough order history never matches.
 */
export type SegmentPredictionCondition =
  | { prediction: 'next_order_within'; days: number }
  | { prediction: 'churn_risk'; levels: ChurnRisk[] }
  | { prediction: 'predicted_value'; op: 'at_least' | 'at_most'; value: number };

export type ChurnRisk = 'low' | 'medium' | 'high';

/** K6: `GET /contacts/:id` `predictions`. Estimates, never facts. */
export interface ContactPrediction {
  /** `contact` = from the contact's own orders; `shop` = one order, the shop's typical rhythm. */
  basis: 'contact' | 'shop';
  gapDays: number;
  expectedNextOrderAt: number;
  churnRisk: ChurnRisk;
  /** Expected orders in the next 365 days. */
  expectedOrders: number;
  /** Spent so far + expected spend in the next 365 days. */
  predictedValue: number;
  spent: number;
  /** Currency of the newest order. */
  currency: string | null;
}

export type SegmentConsentCategory =
  'confirmed_double_optin' | 'single_optin_with_time_and_ip' | 'time_only' | 'no_evidence';

/** One AND-group. Mirrors `segments.repo.ts#SegmentGroup`. */
export interface SegmentGroup {
  labels: string[];
  excludeLabels: string[];
  fields: SegmentFieldCondition[];
  /** Has unsubscribed at least once — Mailchimp's "Afmeldte" (P7). */
  unsubscribed: boolean;
  /** `contacts.source` is one of these (exact match). Fase 7 signup source stats. */
  sources: string[];
  /** `contacts.language` is one of these (two letters, lower case). */
  languages: string[];
  /** `contacts.country` is one of these (ISO 3166-1 alpha-2, upper case). */
  countries: string[];
  /** `contacts.country` is none of these; a contact with no known country matches. */
  excludeCountries: string[];
  /** Clicked / has not clicked within N days. Absent or `null` = no engagement condition. */
  engagement?: SegmentEngagement | null | undefined;
  consentCategories?: SegmentConsentCategory[];
  events?: SegmentEventCondition[];
  purchases?: SegmentPurchaseCondition[];
  predictions?: SegmentPredictionCondition[];
}

/**
 * Mirrors `segments.repo.ts#SegmentFilter` (K2): AND inside a group, OR
 * between groups. The API always returns this shape, also for segments
 * saved before groups existed.
 */
export interface SegmentFilter {
  version: 2;
  groups: SegmentGroup[];
}

export type SegmentLibraryGoal =
  'engagement' | 'deliverability' | 'purchasers' | 'convert' | 'consent';

export interface SegmentLibraryPreset {
  key: string;
  goal: SegmentLibraryGoal;
  name: string;
  description: string;
  filter: SegmentFilter;
}

export interface SegmentLibraryResponse {
  goals: { goal: SegmentLibraryGoal; title: string }[];
  presets: SegmentLibraryPreset[];
}

/** `/api/shared-blocks` (K4): a block saved once and reused with `{{np_block <id>}}`. */
export interface SharedBlock {
  id: string;
  name: string;
  html: string;
  blockJson: string | null;
  createdAt: number;
  updatedAt: number;
  usage?: { templates: number; draftCampaigns: number };
}

/** `/api/custom-objects` (K8): a field of a custom object type. */
export type CustomFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select';

export interface CustomField {
  key: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  /** Select fields only. */
  options?: string[];
}

export type CustomValue = string | number | boolean;

/** `/api/custom-objects/types` (K8): a per-company object type, e.g. "Treatment". */
export interface CustomObjectType {
  id: string;
  name: string;
  slug: string;
  fields: CustomField[];
  /** Records of this type (absent in the per-contact list). */
  recordCount?: number;
  createdAt: number;
  updatedAt: number;
}

/** A record of a custom object type, linked to one contact. */
export interface CustomObjectRecord {
  id: string;
  typeId: string;
  contactId: string;
  values: Record<string, CustomValue>;
  createdAt: number;
  updatedAt: number;
}

/** `GET /api/custom-objects/contacts/:id`: every type with this contact's records. */
export interface ContactCustomObjectsResponse {
  items: { type: CustomObjectType; records: CustomObjectRecord[] }[];
}

/** `GET /api/catalog` and `POST /api/catalog/refresh` (K4): the products the product feed block shows. */
export interface CatalogResponse {
  products: CatalogProduct[];
  /** When the catalogue was last refreshed (ms), or null when it never was. */
  refreshedAt: number | null;
}

/** `POST /api/segments/estimate`: contacts matching, and how many of those can be mailed right now. */
export interface SegmentEstimate {
  matching: number;
  reachable: number;
}

export interface Segment {
  id: string;
  name: string;
  /** `null` if a stored filter no longer parses (segments.ts#segmentJson); the send path fails closed on it. */
  filter: SegmentFilter | null;
  archived: boolean;
  archivedAt: number | null;
  campaignCount: number;
  createdAt: number;
  updatedAt: number | null;
}

/**
 * The primary sender's pause. `reason` never carries the raw SES message:
 * for an SES halt only `ses:<code> status=<n>`, for an auto-pause our own
 * text, otherwise null (docs/api-ui.md).
 */
export interface SenderPaused {
  paused: boolean;
  reason: string | null;
  kind: 'ses_halt' | 'auto_pause' | 'other' | null;
  errorCode: string | null;
  httpStatus: number | null;
  pausedAt: number | null;
  senderId: string | null;
}

/** One sending-health rate; below `minSample` sends auto-pause ignores it. */
export interface HealthRate {
  rate: number;
  warnAt: number;
  pauseAt: number;
  window: string;
  attempted: number;
  minSample: number;
}

export interface HealthSummary {
  bounce: HealthRate;
  complaint: HealthRate;
  warmup: {
    day: number;
    totalDays: number;
    limitToday: number;
    sentToday: number;
    schedule: { day: number; limit: number }[];
    target: number | null;
    paused: boolean;
    startedAt: number | null;
  } | null;
  ses: { mode: 'fake' | 'live' | 'unknown' };
  unknownMessages: number;
  queue: { queued: number; retryable: number };
  unsubscribeLog: { lastEntryAt: number | null; count7d: number };
  killSwitch: { engaged: boolean };
  senderPaused: SenderPaused;
  stuckFlowRuns: number;
}

/** K6: `GET /api/stats/deliverability` (rates are fractions, 0.001 = 0.1 %). */
export type DeliverabilityStatus = 'green' | 'amber' | 'red' | 'insufficient';
export interface DeliverabilityScore {
  sent: number;
  bounceRate: number;
  complaintRate: number;
  unsubscribeRate: number;
  bounce: DeliverabilityStatus;
  complaint: DeliverabilityStatus;
  unsubscribe: DeliverabilityStatus;
  overall: DeliverabilityStatus;
}

export interface CampaignStatsListItem {
  id: string;
  name: string;
  status: CampaignStatus;
  sentAt: number | null;
  sent: number;
  delivered: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
}

export interface CampaignStatsDetail {
  id: string;
  name: string;
  sent: number;
  delivered: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
  opensTracked: boolean;
  clicksPerHour: { hourStart: number; clicks: number }[];
  topLinks: { url: string; clicks: number; uniqueClicks: number }[];
  /** Fase 6: present only when the campaign has A/B enabled. */
  ab: CampaignAbStatsInfo | null;
  /** Sending cost, revenue and ROI of the campaign, all its orders (26 Oct 2026). */
  roi: SendingRoi;
  /** Who bought from the campaign: its paid orders, newest first, at most 100. */
  orders: CampaignOrder[];
}

/**
 * One paid order attributed to a campaign (`CampaignStatsDetail.orders`).
 * `amount` is net of refunds; `contactId`/`email` are null once the
 * contact was deleted.
 */
export interface CampaignOrder {
  orderId: string;
  orderedAt: number;
  amount: number;
  currency: string;
  contactId: string | null;
  email: string | null;
}

/**
 * `GET /api/stats/sending-roi` and `CampaignStatsDetail.roi` (26 Oct 2026):
 * `sent` = mails handed to Amazon SES, `cost = sent × costPerThousand / 1000`
 * and `revenue` (attributed orders net of refunds), both in `currency`;
 * `roi = revenue ÷ cost`, `null` when nothing was spent.
 */
export interface SendingRoi {
  sent: number;
  costPerThousand: number;
  cost: number;
  orders: number;
  revenue: number;
  /** Attributed orders still on hold (awaiting payment): beside the revenue, never in it or the ROI. */
  onHoldOrders: number;
  onHoldRevenue: number;
  roi: number | null;
  currency: string;
}

/** `GET`/`PUT /api/settings/sending-cost`: the price of 1,000 emails, in `currency`. */
export interface SendCostSetting {
  costPerThousand: number;
  currency: string;
  updatedAt: number;
}

/** `GET`/`PUT /api/settings/popup`: the sitewide signup popup (migration 0049). */
export type PopupTrigger = 'delay' | 'scroll' | 'exit_intent';
export type PopupLanguage = 'da' | 'de' | 'fr' | 'it' | 'nl';
export type PopupFont = 'poppins' | 'roboto' | 'system';

/** One language's texts; a missing field shows the English text. */
export interface PopupTexts {
  headline?: string;
  body?: string;
  buttonText?: string;
  successText?: string;
}

/** `/settings/tracking` (K1): the switch for browse tracking of known contacts. */
export interface TrackingSettings {
  enabled: boolean;
  /** A product counts as low on stock at this quantity or below (1 to 1000). */
  lowStockThreshold: number;
  updatedAt: number;
  embedCode: string | null;
}

export type PopupTargetMode = 'all' | 'url' | 'product' | 'cart';

export interface PopupSettings {
  enabled: boolean;
  headline: string;
  body: string;
  buttonText: string;
  successText: string;
  /** `null`: the default signup list. */
  listId: string | null;
  trigger: PopupTrigger;
  delaySeconds: number;
  scrollPercent: number;
  frequencyDays: number;
  showOnMobile: boolean;
  imageUrl: string | null;
  /** `#rrggbb`. */
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  cornerRadius: number;
  headingFont: PopupFont;
  dimBackground: boolean;
  showNameField: boolean;
  /** Shown after a signup; `null`: no code. */
  couponCode: string | null;
  translations: Partial<Record<PopupLanguage, PopupTexts>>;
  /** Where it may show: every page, an address containing a text, product pages or the cart page. */
  targetMode: PopupTargetMode;
  /** Only used with `targetMode` 'url'. */
  targetUrlPart: string | null;
  updatedAt: number;
  /** The one-line script tag for the shop's theme; `null` when no news domain is set up. */
  embedCode: string | null;
}

/** `/api/forms` (K5, migration 0066): the tenant's signup forms. The legacy popup is the entry `popup`. */
export type FormType = 'popup' | 'flyout' | 'fullscreen' | 'embedded' | 'bar';
export type FormStatus = 'draft' | 'live' | 'off';
export type FormTrigger = 'immediate' | 'delay' | 'scroll' | 'exit_intent';

export interface FormTexts {
  headline: string;
  body: string;
  buttonText: string;
  successText: string;
  nextText: string;
  step2Headline: string;
  step2Body: string;
  skipText: string;
  birthdayText: string;
}

export interface FormConfig {
  texts: FormTexts;
  translations: Record<string, Partial<FormTexts>>;
  imageUrl: string | null;
  couponCode: string | null;
  design: {
    backgroundColor: string;
    textColor: string;
    buttonColor: string;
    cornerRadius: number;
    headingFont: PopupFont;
    dimBackground: boolean;
  };
  targeting: {
    trigger: FormTrigger;
    delaySeconds: number;
    scrollPercent: number;
    includePaths: string[];
    excludePaths: string[];
    countries: string[];
    languages: string[];
    visitor: 'all' | 'new' | 'returning';
    showOnMobile: boolean;
    showOnDesktop: boolean;
    frequencyDays: number;
  };
  steps: { multiStep: boolean; askName: boolean; askBirthday: boolean };
  ab: {
    enabled: boolean;
    splitPercent: number;
    winner: 'a' | 'b' | null;
    variantB: {
      texts: Partial<FormTexts>;
      translations: Record<string, Partial<FormTexts>>;
      design: Partial<FormConfig['design']>;
    };
  };
}

export interface SignupForm {
  id: string;
  name: string;
  type: FormType;
  status: FormStatus;
  /** `null`: the default signup list. */
  listId: string | null;
  countViews: boolean;
  config: FormConfig;
  createdAt: number;
  updatedAt: number;
  /** Script tag (plus the `div` for an embedded form); `null` without a news domain. */
  embedCode: string | null;
}

export type SignupFormInput = Pick<
  SignupForm,
  'name' | 'type' | 'listId' | 'countViews' | 'config'
>;

export interface FormStatRow {
  formId: string;
  variant: 'a' | 'b';
  views: number;
  signups: number;
}

export interface SignupFormsResponse {
  forms: SignupForm[];
  popup: { id: 'popup'; name: string; type: 'popup'; status: 'live' | 'off' };
  stats: FormStatRow[];
  days: number;
  scriptTag: string | null;
}

/** `GET /api/stats/overview` (Fase 7 item 7) — summed KPIs for `[from, to)`. */
export interface OverviewStats {
  sent: number;
  delivered: number;
  clicked: number;
  /** Mails clicked only by bots; in `clicked` only with `bots=include`. */
  botClicked: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
}

export interface CampaignAbVariantStats {
  recipients: number;
  uniqueClicks: number;
}

export interface CampaignAbStatsInfo {
  splitPercent: number;
  waitHours: number;
  subjectB: string | null;
  /** K6: variant B had its own content (not only its own subject line). */
  contentDiffers?: boolean;
  /** K6: true = the cron sends the winner to the rest; false = an admin picks it. */
  autoSend?: boolean;
  metric?: 'clicks' | 'opens';
  winnerVariant: 'a' | 'b' | null;
  winnerDecidedAt: number | null;
  noSignificantWinner: boolean;
  stats: { a: CampaignAbVariantStats; b: CampaignAbVariantStats };
}

/** `GET /api/stats/sources` row (fase 7 item 5, "Tilmeldingskilde som statistik"). */
export interface SourceStat {
  /** Exact `contacts.source` value, or `"(unknown)"` for a null/empty source. */
  source: string;
  newContacts: number;
  unsubscribed: number;
  /** Net revenue (total_amount - refunded_amount) from valid orders, attributed to this source's contacts. */
  revenue: number;
}

export interface SourceStatsPeriod {
  from: number;
  to: number;
}

export type SuppressionReason = 'unsubscribed' | 'hard_bounce' | 'complaint' | 'manual';
export type UnsubscribeSource = 'header_one_click' | 'link_click' | 'manual' | 'import';

export type UnsubscribeDetailSource =
  'header_one_click' | 'link_click' | 'preference_centre' | 'manual';
export type UnsubscribeReason =
  'too_many' | 'not_relevant' | 'not_customer' | 'by_mistake' | 'other';

/** `GET /api/suppressions`: counts and dates only, never an address or its hash. */
export interface SuppressionsOverviewResponse {
  counts: Record<SuppressionReason, number>;
  unsubscribesBySource: Record<UnsubscribeSource, number>;
  /** `total`: every kept unsubscribe, with or without a reason. */
  reasons: { byReason: Record<UnsubscribeReason, number>; total: number };
}

/** One unsubscribe (`unsubscribe_details`). */
export interface UnsubscribeDetail {
  id: string;
  /** Kept since 26 Oct 2026 for an unsubscribe from a mail; `null` otherwise or once removed. */
  email: string | null;
  occurredAt: number;
  source: UnsubscribeDetailSource;
  reason: UnsubscribeReason | null;
  reasonAt: number | null;
  campaign: { id: string; name: string } | null;
  language: string | null;
  country: string | null;
  signupSource: string | null;
  /** The day the contact was created (midnight UTC). */
  subscribedOn: number | null;
}

/** `GET /api/suppressions/unsubscribes`: newest first, keyset-paged. */
export interface UnsubscribesListResponse {
  items: UnsubscribeDetail[];
  total: number;
  nextCursor: string | null;
}

/** Why a mail came back; mirrors `packages/core/src/bounces/reason.ts#BounceReason`. */
export type BounceReason =
  'typo' | 'corrected' | 'nodomain' | 'nobox' | 'suppressed' | 'full' | 'refused' | 'spam';

/** One bounce or spam complaint SES reported for one address (migration 0045). */
export interface BounceDetail {
  id: string;
  email: string;
  occurredAt: number;
  kind: 'bounce' | 'complaint';
  /** SES `bounceType` (Permanent, Transient, Undetermined) or a complaint's feedback type; `null` before 26 Oct 2026. */
  bounceType: string | null;
  subType: string | null;
  /** The receiving server's answer, as SES reports it. */
  diagnostic: string | null;
  campaign: { id: string; name: string } | null;
  reason: BounceReason;
  /** For `typo`: the domain it probably should be. */
  suggestedDomain: string | null;
}

/** `GET /api/suppressions/bounces/summary`: counts per reason and the mails that have bounces. */
export interface BounceSummaryResponse {
  total: number;
  reasons: Record<BounceReason, number>;
  campaigns: { id: string; name: string }[];
}

/** `GET /api/suppressions/bounces`: newest first, keyset-paged. */
export interface BouncesListResponse {
  items: BounceDetail[];
  total: number;
  nextCursor: string | null;
}

/** Paid orders (processing, on-hold, completed) for one address. */
export interface AddressOrders {
  count: number;
  lastOrderedAt: number | null;
}

/**
 * `GET /api/suppressions/bounces/:id/contact`: who the bounced address belongs to and, for a
 * typo, what is known about the corrected address. Mirrors
 * `packages/core/src/db/tenant/bounceContact.repo.ts#BounceContactOverview`.
 */
export interface BounceContactOverview {
  /** The contact that has the bounced address, or null (deleted, already fixed, or never a contact). */
  contact: {
    id: string;
    status: ContactStatus;
    source: string | null;
    consentCategory: string | null;
    language: string | null;
    country: string | null;
    createdAt: number;
    /** The earliest consent we hold (for an import: Mailchimp's opt-in time). */
    signedUpAt: number | null;
    orderSignupAt: number | null;
  } | null;
  /** Paid orders by the contact or placed with the bounced address. */
  orders: AddressOrders;
  /** Only for a typo: the suggested address and what is known about it. */
  corrected: {
    email: string;
    orders: AddressOrders;
    /** A contact that already has the corrected address, or null. */
    contactId: string | null;
  } | null;
  /** The newsletter that bounced, if it can go once more to the fixed address. */
  resend: BounceResendOffer | null;
}

/** Mirrors `packages/core/src/db/tenant/bounceContact.repo.ts#BounceResendOffer`. */
export interface BounceResendOffer {
  campaign: { id: string; name: string; sentAt: number };
  messageId: string;
  /** Why it can't be sent (any more), or null when it can. */
  blocked: 'already_resent' | 'too_old' | 'contact_unavailable' | null;
}

/** How a resend of the bounced newsletter went (`apps/app/src/routes/suppressions.ts`). */
export type BounceResendResult =
  | { status: 'sent' }
  /** SES gave no clear answer: it may have gone out, and it is never sent again. */
  | { status: 'unknown' }
  | { status: 'not_sent'; reason: string };

/** `POST /api/suppressions/bounces/:id/fix-address` (admin + step-up). */
export interface FixAddressResponse {
  contactId: string;
  ordersLinked: number;
  /** null when the admin chose to send nothing now. */
  resend: BounceResendResult | null;
}

/** `POST /api/suppressions/bounces/:id/resend` (admin + step-up). */
export interface BounceResendResponse {
  resend: BounceResendResult;
}

/** `POST /api/suppressions/lookup` (admin): what is known about one address. */
export interface SuppressionLookupResponse {
  suppression: { reason: SuppressionReason; since: number } | null;
  lastUnsubscribe: { at: number; source: UnsubscribeSource } | null;
  contactStatus: ContactStatus | null;
}

export interface SourceStatsResponse {
  items: SourceStat[];
  period: SourceStatsPeriod;
}

export type ContactStatus = 'active' | 'suppressed';

export interface ContactListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /** Two-letter language code, or null when unknown. */
  language: string | null;
  /** ISO 3166-1 alpha-2 country code, or null when unknown. */
  country: string | null;
  status: ContactStatus;
  /** Null when no consent was recorded, e.g. a buyer put on the list by an order (`orderSignupAt`). */
  consentCategory: string | null;
  /** When an order put the buyer on the newsletter, or null. */
  orderSignupAt: number | null;
  createdAt: number;
  lastSentAt: number | null;
  /** Newest order that counts as a purchase (processing, on-hold, completed), or null. */
  lastOrderAt: number | null;
}

/** Where a name, language or country came from. `self` = the contact's own preference page. */
export type AttributeSource = 'import' | 'signup' | 'order' | 'self';

export interface ContactDetail extends ContactListItem {
  source: string;
  /** When an order put the buyer on the newsletter (docs/decisions.md "Købere kommer på nyhedsbrevet ved køb"), or null. */
  orderSignupAt: number | null;
  nameSource: AttributeSource | null;
  languageSource: AttributeSource | null;
  countrySource: AttributeSource | null;
  city: string | null;
  postalCode: string | null;
  region: string | null;
  timezone: string | null;
  /** What Mailchimp knew (import only); null for contacts that were not imported. */
  mailchimp: {
    leid: string | null;
    euid: string | null;
    memberRating: number | null;
    lastChangedAt: number | null;
    importedAt: number;
  } | null;
  consentText: string;
  /** Version af samtykketeksten (migration 0009); null for ældre rækker. */
  consentTextVersion?: string | null;
  consentAt: number | null;
}

export interface ConsentEntry {
  at: number;
  action: string;
  source: string;
  text: string;
  /** Version af samtykketeksten (`v1`, `v2`, ...); null for ældre rækker. */
  textVersion?: string | null;
}

export type ConsentTextKind = 'form' | 'checkout';

/** `/api/consent-texts` (docs/api-ui.md "Samtykketekster"). */
export interface ConsentText {
  version: string;
  text: string;
  /** Hvor teksten vises: tilmeldingsformularen eller kassens afkrydsning (migration 0035). */
  kind: ConsentTextKind;
  /** Sprogkode (`da`, `de`, ...), eller null for standardteksten til sprog uden egen tekst (migration 0042). */
  language: string | null;
  createdAt: number;
  firstUsedAt: number | null;
  /** true når en tilmelding eller samtykkelog-række bruger versionen: så kan den ikke ændres. */
  locked: boolean;
}

export interface HistoryEntry {
  at: number;
  type: 'sent' | 'click' | 'unsubscribe' | 'bounce' | 'complaint' | 'delivery';
  campaignId: string;
  campaignName: string;
  detail: string | null;
}

export interface ContactsListResponse {
  items: ContactListItem[];
  nextCursor: string | null;
  total: number;
}

export interface ContactDetailResponse {
  contact: ContactDetail;
  consent: ConsentEntry[];
  history: HistoryEntry[];
  /** fase 5: auto + manual mærkater (docs/api-ui.md "Kundemærkater"); top level, like apps/app/src/routes/contacts.ts. */
  labels: ContactLabel[];
  /** K8: the contact's own custom fields (key to value). */
  fields?: Record<string, string>;
  /** K6: estimates from order history; `null` without enough history or after a profiling objection. */
  predictions?: ContactPrediction | null;
}

/** K8: body of `PATCH /contacts/:id`. `null` clears; consent is not editable. */
export interface ContactEditInput {
  firstName?: string | null;
  lastName?: string | null;
  language?: string | null;
  country?: string | null;
  customFields?: Record<string, string | null> | undefined;
  labels?: string[];
}

/** K8: one chunk's report from `POST /contacts/import`. */
export interface CsvImportReport {
  received: number;
  created: number;
  updated: number;
  unchanged: number;
  skippedInvalidEmail: number;
  skippedSuppressed: number;
  skippedDuplicateInChunk: number;
  /** New contacts marked "may get one confirmation email" (invite mode). */
  invitable: number;
  notMailable: number;
  labelsApplied: number;
  labelsRefused: number;
  languagesRefused: number;
  countriesRefused: number;
}

/** `GET /contacts/import/invite/status`: is the invite feature on, and how far is one import. */
export interface CsvInviteStatus {
  enabled: boolean;
  counts: { invitable: number; sending: number; invited: number; failed: number; skipped: number };
}

/** `POST /contacts/import/invite`: one chunk's result. Counts only. */
export interface CsvInviteResult {
  ok: true;
  sent: number;
  failed: number;
  skipped: number;
  skippedBy: Record<string, number>;
  remaining: number;
}

/** K8: one row of `GET /audit`. */
export interface AuditEntry {
  id: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown>;
  createdAt: number;
}

export interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
}

export interface CampaignTagCount {
  tag: string;
  count: number;
}

/** K8: `GET /coupons`. */
export interface CouponOverview {
  personal: {
    campaignId: string;
    name: string;
    status: string;
    percent: number | null;
    prefix: string | null;
    issued: number;
    redeemed: number;
  }[];
  automationIssued: number;
  shared: {
    code: string;
    orders: number;
    /** One entry per order currency, largest first. */
    discounts: { currency: string; amount: number }[];
  }[];
  ordersCapped: boolean;
  redeemedCapped: boolean;
}

export interface BrandKit {
  logoUrl: string | null;
  primaryColor: string;
  textColor: string;
  backgroundColor: string;
  footerHtml: string;
  /** "Sender details" for {{ company_address }}: who is sending, e.g. "NoPain® Cream". A postal address is not required (6/10-2026). */
  companyAddress: string | null;
  /** Optional https legal notice / Impressum link for {{ legal_notice_url }}. */
  legalNoticeUrl: string | null;
  defaultSenderId: string | null;
  utm: { enabled: boolean; source: string; medium: string };
  /** The tenant's own Trustpilot review page (anmeldelsesflow 28/9) — https://…trustpilot.com/… or null if not set up yet. */
  trustpilotReviewUrl: string | null;
  /** Internal address that gets the follow-up notice for a rating of 3 or below, or any feedback text. */
  reviewCustomerServiceEmail: string | null;
  updatedAt: number;
}

export type ReviewItemStatus = 'open' | 'resolved';

/** One follow-up item from the anmeldelsesflow (28/9) — a low rating and/or free-text feedback. */
export interface ReviewItem {
  id: string;
  orderId: string;
  contactId: string | null;
  rating: number | null;
  feedback: string;
  status: ReviewItemStatus;
  createdAt: number;
  updatedAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
}

export interface ReviewItemListResponse {
  items: ReviewItem[];
}

/** Aggregate rating distribution — no contact data, safe to show on a dashboard. */
export interface ReviewStats {
  total: number;
  byRating: Record<'1' | '2' | '3' | '4' | '5', number>;
}

export type CheckStatus = 'ok' | 'warn' | 'fail';

export interface PreflightCheck {
  id: string;
  status: CheckStatus;
  title: string;
  message: string;
  items?: string[];
}

export interface PreflightResult {
  ok: boolean;
  sizeBytes: number;
  checks: PreflightCheck[];
  previews: { html: string; text: string };
}

export interface MessageCounts {
  queued: number;
  sending?: number;
  sent: number;
  /** SES reported back: these moved on from `sent`. */
  delivered?: number;
  bounced?: number;
  complained?: number;
  retryable: number;
  /** Given up on after the last retry (expired flow mails are not in here). */
  failed: number;
  /** Flow mails dropped unsent: they waited more than 48 hours in the queue, or an earlier mail of the same flow for that contact did. Only flow-step campaigns have any. */
  expired?: number;
  unknown: number;
  unsubscribed?: number;
  /** Every message of the campaign, whatever its status. */
  total?: number;
  /** Still to be sent (queued, retrying, held-back): what an approval or a resume covers. The server counts it. */
  toSend?: number;
  [key: string]: number | undefined;
}

// --- Kundemærkater (fase 5, docs/api-ui.md "Kundemærkater") ---

export type AutoLabel =
  | 'new_customer'
  | 'repeat'
  | 'loyal'
  | 'platinum'
  | 'vip'
  | 'inactive'
  | 'lost'
  | 'subscriber_only';

export interface ContactLabel {
  label: string;
  source: 'auto' | 'manual';
}

export interface LabelSettings {
  loyalOrderCount: number;
  platinumThresholdAmount: number;
  vipThresholdAmount: number;
  inactiveAfterDays: number;
  lostAfterDays: number;
  softOptinMaxDays: number;
  singleOptinCountsAsOptin: boolean;
  timeOnlyCountsAsOptin: boolean;
  currency: string;
  updatedAt: number;
}

/** One row of `GET /api/labels`'s `counts` — mirrors `packages/core/src/labels/compute.ts#LabelOverviewEntry`. `label` is the raw label string: an `AutoLabel` for one of the 8 automatic tags, or any manual-label slug (P7, `docs/decisions.md` "Mailchimp tags become manual labels"). */
export interface LabelCountEntry {
  label: string;
  count: number;
}

/** `counts` is an ARRAY (one row per label that has at least one contact, auto or manual), not a `Record` — the backend never emits a zero-count row for a manual label nobody has. See `apps/app/src/routes/labels.test.ts`. */
export interface LabelsOverviewResponse {
  counts: LabelCountEntry[];
  settings: LabelSettings;
}

// --- Automatiseringer (fase 5, docs/api-ui.md "Automatiseringer") ---

export type LegalBasis = 'optin' | 'soft_optin' | 'customer' | 'none';

export type TriggerType =
  | 'contact.confirmed'
  | 'order.created'
  | 'order.completed'
  | 'order.first'
  | 'cart.abandoned'
  | 'date.birthday'
  | 'date.first_order_anniversary'
  | 'inactivity'
  | 'label.entered'
  | 'label.left'
  | 'browse.abandoned'
  | 'checkout.abandoned'
  | 'product.price_drop'
  | 'product.low_stock';

export type FlowStepType =
  | 'wait'
  | 'condition'
  | 'create_coupon'
  | 'send_template'
  | 'update_contact'
  | 'split'
  | 'ab_split'
  | 'end'
  | 'internal_alert'
  | 'webhook';

/** The languages a flow mail can be written in (core `FLOW_LANGUAGES`, `packages/core/src/flows/types.ts`). */
export const FLOW_LANGUAGES = [
  { code: 'da', name: 'Danish' },
  { code: 'de', name: 'German' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
] as const;

/** One language's own template and subject on a `send_template` step (`step.variants`). */
export interface FlowLanguageVariant {
  language: string;
  templateId: string | null;
  subject: string;
}

/** Kept loose (`Record<string, unknown>` per step) — the web layer never renders/edits step internals, only passes definitions through. */
export interface FlowDefinition {
  steps: ({ id: string; type: FlowStepType } & Record<string, unknown>)[];
}

export interface Automation {
  id: string;
  slug: string;
  name: string;
  builtInKey: string | null;
  triggerType: TriggerType;
  triggerConfig: unknown;
  allowedLegalBasis: LegalBasis[];
  allowReentry: boolean;
  stopOnPurchase: boolean;
  currentVersion: number;
  enabled: boolean;
  enabledBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface AutomationStats {
  enrolled: number;
  active: number;
  completed: number;
  stopped: number;
  sent: number;
  /** Queued mails dropped unsent: they waited more than 48 hours, or an earlier mail of the same flow for that contact did. */
  expired: number;
  /** Times a mail of this flow was put off a day because the contact already had 2 mails in the last 7 days. */
  deferredByFrequencyCap: number;
  /** Mails of this flow never sent because the contact was still at the frequency cap after 3 days. */
  skippedByFrequencyCap: number;
  revenue: number;
  /** ISO 4217 reporting currency of the tenant, e.g. "EUR". */
  currency: string;
}

export interface AutomationListItem extends Automation {
  stats: AutomationStats;
}

export interface AutomationVersion {
  version: number;
  definition: FlowDefinition;
  createdBy: string;
  createdAt: number;
}

export interface AutomationTimelineEntry {
  stepId: string;
  type: FlowStepType;
  offsetSeconds: number;
  description: string;
  purpose?: string;
  check?: string;
  onFail?: string;
}

export interface AutomationsListResponse {
  automations: AutomationListItem[];
}

/** Numbers for one step (core `StepNumbers`, `flows/stepStats.ts`). */
export interface FlowStepNumbers {
  entered: number;
  sent: number;
  opened: number;
  clicked: number;
  ordered: number;
  revenue: number;
}

/** `stepStats` of `GET /api/automations/:id`. A step nobody reached is absent from `steps`. */
export interface FlowStepStats {
  steps: Record<string, FlowStepNumbers>;
  /** Keyed `"<ab_split step id>:<path id>"`: runs sent down that path. */
  paths: Record<string, number>;
}

export interface AutomationDetailResponse {
  automation: Automation;
  /** Numbers per step; absent from older servers and the plain mock. */
  stepStats?: FlowStepStats;
  definition: FlowDefinition;
  versions: AutomationVersion[];
  timeline: AutomationTimelineEntry[];
  stats: AutomationStats;
  /** True for a built-in flow that comes with NoPain's designed mails. */
  hasDesignTemplates?: boolean;
  /** Every NoPain design mail of the flow (English first), created one request each. */
  designMails?: { language: string; stepId: string }[];
}

export interface AutomationResponse {
  automation: Automation | null;
}

export interface AutomationSaveResponse {
  automation: Automation;
  definition: FlowDefinition;
}

/** `POST /api/automations/builtins/:key/install`'s known keys (packages/core/src/flows/builtins/index.ts). */
export type BuiltInFlowKey =
  | 'welcome'
  | 'post_first_purchase'
  | 'replenishment'
  | 'abandoned_cart'
  | 'winback'
  | 'review'
  | 'birthday'
  | 'anniversary'
  | 'sunset';

/** `GET /api/automations/builtins/:key/preview`: what a built-in does before it is installed. */
export interface BuiltinPreviewResponse {
  builtin: {
    key: BuiltInFlowKey;
    slug: string;
    name: string;
    triggerType: TriggerType;
    triggerConfig: Record<string, unknown>;
    allowedLegalBasis: LegalBasis[];
    allowReentry: boolean;
    stopOnPurchase: boolean;
  };
  timeline: AutomationTimelineEntry[];
  /** One per mail step, in order. `previewHtml` is null when the flow has no designed mail for it. */
  mails: {
    stepId: string;
    name: string | null;
    subject: string;
    purpose: string;
    previewHtml: string | null;
  }[];
}

// --- Skabeloner (fase 5, docs/api-ui.md "Skabeloner") ---

export interface TemplateUsedBy {
  campaigns: number;
  automations: number;
}

/** `html` = edited as HTML source; `blocks` = edited in the block editor. Fixed for the template's life. */
export type TemplateKind = 'html' | 'blocks';

export interface TemplateListItem {
  id: string;
  name: string;
  kind: TemplateKind;
  updatedAt: number;
  usedBy: TemplateUsedBy;
}

export interface Template {
  id: string;
  name: string;
  kind: TemplateKind;
  /** Editorens blok-model (`lib/blocks.ts`) — API'et gemmer den uigennemsigtigt. */
  blockJson: unknown;
  html: string;
  textFallback: string | null;
  createdAt: number;
  updatedAt: number;
  /** Section editor state; only on single reads of an HTML template. */
  sections?: StoredSectionEdits | null;
}

/** An image uploaded for use in mails (`/api/media`, see docs/api-ui.md). */
export interface MediaImage {
  id: string;
  /** Public URL on the media domain. This is what goes into the block's image field. */
  url: string;
  contentType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
  width: number;
  height: number;
  byteSize: number;
  name: string | null;
  createdAt: number;
}

export interface MediaListResponse {
  images: MediaImage[];
  /** Pass as `before` to get the next (older) page; null when there is none. */
  nextBefore: string | null;
}

export interface MediaUploadResponse {
  image: MediaImage;
}

export interface TemplatesListResponse {
  templates: TemplateListItem[];
}

export interface TemplateDetailResponse {
  template: Template;
}

export interface TemplatePreviewResponse {
  valid: boolean;
  errors: { code: string; message?: string }[];
  sanitizedHtml: string | null;
  previewHtml: string;
  sizeBytes: number;
}

// --- HTML import (`POST /api/templates/html-check`, mirrors packages/core html-import-report.ts) ---

export interface HtmlImportProblem {
  code: string;
  message: string;
  tag?: string;
  count?: number;
}

export interface HtmlImportTranslation {
  tag: string;
  to: string;
  count: number;
}

export interface HtmlRemovedItem {
  /** `block`: a whole piece of non-email markup, named in plain words (Mailchimp's archive bar). */
  kind: 'tag' | 'attribute' | 'comment' | 'block';
  name: string;
  count: number;
  note?: string;
}

export interface HtmlImportedImage {
  src: string;
  alt: string | null;
  kind: 'hosted' | 'base64' | 'relative' | 'merge';
  host: string | null;
}

export interface HtmlImportReport {
  /** The input with Mailchimp tags translated: the working copy the editor holds. Empty when too large to send back. */
  html: string;
  valid: boolean;
  problems: HtmlImportProblem[];
  translations: HtmlImportTranslation[];
  removed: HtmlRemovedItem[];
  images: {
    total: number;
    hosted: number;
    base64: number;
    relative: number;
    mailchimpHosted: number;
    /** `hosted` images that already point at our own media domain. */
    ownHosted: number;
    hosts: { host: string; count: number; mailchimp: boolean }[];
    items: HtmlImportedImage[];
  };
  footer: {
    unsubscribeLink: boolean;
    /** How the footer names the sender (core `footerSenderKind`); `legal_notice` = only via a legal notice / own-site link. */
    sender: 'merge_field' | 'written' | 'legal_notice' | 'missing';
    /** True when the mail links a legal notice / Impressum page. */
    legalNotice: boolean;
  };
  sizeBytes: number;
  maxBytes: number;
  /** Sanitised HTML with sample merge values, for display in a sandboxed frame only. */
  previewHtml: string | null;
}

// --- Copy images to our own storage (`POST /api/media/rehost`, mirrors packages/core templates/rehost.ts) ---

export type RehostFailureCode =
  | 'invalid_url'
  | 'not_https'
  | 'credentials'
  | 'port'
  | 'ip_literal'
  | 'host_not_allowed'
  | 'redirect_refused'
  | 'too_many_redirects'
  | 'http_status'
  | 'fetch_failed'
  | 'invalid_data'
  | 'unsupported_type'
  | 'empty'
  | 'too_large'
  | 'quota_exceeded';

export interface RehostItemResult {
  /** Stable id of the image across rounds; failed ones are sent back as `skip`. */
  key: string;
  kind: 'base64' | 'hosted';
  /** The address, or "Embedded JPEG image (about 480 KB)". */
  label: string;
  alt: string | null;
  status: 'copied' | 'failed';
  url?: string;
  imageId?: string;
  created?: boolean;
  byteSize?: number;
  contentType?: string;
  code?: RehostFailureCode;
  message?: string;
}

export interface RehostResponse {
  /** The HTML with every copied image pointing at the media domain. */
  html: string;
  results: RehostItemResult[];
  /** Images not tried yet (and not skipped): ask again while this is above zero. */
  remaining: number;
}

export interface HtmlCheckResponse {
  report: HtmlImportReport;
}

/** `GET /api/settings/woo` — never the key/secret, see `apps/app/src/routes/wooSettings.ts`. */
export interface WooSettingsStatus {
  configured: boolean;
  pluginUrl: string | null;
  lastRotatedAt: number | null;
}

/** One product of the connected shop, as `GET /api/woo/products` returns it (`apps/app/src/routes/wooProducts.ts`). Prices are plain decimal strings. */
export interface ShopProduct {
  id: string;
  name: string;
  price: string;
  regularPrice: string;
  /** "" unless the product is on sale. */
  salePrice: string;
  currency: string;
  imageUrl: string | null;
  permalink: string;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  variationsCount: number;
}

export interface ShopProductsResponse {
  products: ShopProduct[];
  page: number;
  hasMore: boolean;
}

/** `POST /api/woo/products/image` — a failed copy is a normal answer, not an error. */
export type ShopProductImageResponse =
  { status: 'copied'; url: string } | { status: 'failed'; message: string };

/** `GET /api/settings/woo/webhook` — never the secret. */
export interface WooWebhookStatus {
  configured: boolean;
  lastRotatedAt: number | null;
  /** `https://<public host>/woo/webhook`, or `null` while the account has no public domain. */
  deliveryUrl: string | null;
}

/** `GET /api/settings/woo/order-signup` — whether buyers join the newsletter when they order. */
export interface WooOrderSignupStatus {
  enabled: boolean;
  /** Only orders placed at or after this put their buyer on the list; null if never switched on. */
  enabledAt: number | null;
  updatedAt: number;
}

// --- Brugere/sikkerhed: admin-udløst 2FA/adgangskode-nulstilling og
// sessions-tilbagekaldelse (runbook MANGLER item 4/3, apps/app/src/routes/
// {users,session}.ts) ---

/** `GET /api/users` — one row per member of the caller's own tenant. */
export interface TenantUser {
  id: string;
  email: string;
  role: Role;
  totpEnabled: boolean;
  disabledAt: number | null;
  createdAt: number;
  /** K8: the member's custom role, or null. */
  customRoleId?: string | null;
}

/** `GET /api/api-tokens` — one row per token; the hash and plaintext are never returned. */
export interface ApiTokenRow {
  id: string;
  name: string;
  userId: string;
  userEmail: string;
  role: 'editor' | 'viewer';
  createdAt: number;
  expiresAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

/** `POST /api/api-tokens` — the plaintext `token` is returned this once, never stored. */
export interface CreateApiTokenResponse {
  token: string;
  apiToken: {
    id: string;
    name: string;
    userId: string;
    role: 'editor' | 'viewer';
    expiresAt: number;
  };
}

/** `POST /api/invites` — the plaintext token is returned this once, never stored. */
export interface CreateInviteResponse {
  status: 'success';
  invitationId: string;
  email: string;
  role: Role;
  expiresAt: number;
  inviteToken: string;
}

/** One row of `GET /api/invites`. */
export interface Invitation {
  id: string;
  email: string;
  role: Role;
  expiresAt: number;
  acceptedAt: number | null;
  createdAt: number;
}

/** `POST /api/invites/accept`: a new user gets a session (like login), an existing account is only added. */
export type AcceptInviteResponse =
  MeResponse | { status: 'membership_added'; tenantId: string; role: Role };

/**
 * `POST /api/users/:id/reset-access` on success (redesigned 2026-09-29
 * after a security review: one action, no token ever returned to the
 * admin — it is mailed only to the target's own address). The admin sees
 * only that mail was sent. On a mail failure (re-review fix 2026-09-29)
 * the route instead answers `{ error: 'mail_failed' }` with HTTP 502 —
 * the reset was issued and the target's sessions revoked either way, but
 * the admin must retry (a fresh token invalidates the old one).
 */
export interface MemberResetAccessResponse {
  status: 'sent';
}

/**
 * `POST /api/auth/reset-access` on success — the PUBLIC redeem endpoint
 * the target's own browser calls from `/nulstil-adgang` with the token
 * read out of the URL fragment.
 */
export interface CompleteAccessResetResponse {
  status: 'success';
}

/** `POST /api/session/revoke-all` on success. */
export interface RevokeAllSessionsResponse {
  status: 'success';
  revokedCount: number;
}

// --- Mailchimp-import (P7, docs/decisions.md "Mailchimp-import 28/9") ---

export type ImportJobKind = 'mailchimp_import' | 'mailchimp_unsubscribe_sync';
export type ImportJobStatus = 'draft' | 'running' | 'done' | 'failed' | 'cancelled';

/** Aggregate counts only — never an email, name or IP (mirrors packages/core/src/import/types.ts). */
export interface ImportReport {
  filesProcessed: number;
  totalRows: number;
  byStatus: {
    subscribed: number;
    pending: number;
    unsubscribed: number;
    cleaned: number;
    nonsubscribed: number;
    unknown: number;
  };
  contactsCreated: number;
  consentCategoryCounts: Record<string, number>;
  orderMatchRequiredCount: number;
  suppressionsAdded: { unsubscribed: number; hard_bounce: number };
  skippedAlreadySuppressed: number;
  duplicatesSkipped: number;
  rowErrors: number;
  /** Name, language and country columns (the API fills defaults for jobs stored before they existed). */
  attributes: ImportAttributeReport;
}

/** Mirrors `packages/core/src/import/types.ts#ImportAttributeReport` (header names and counts, never values). */
export interface ImportAttributeReport {
  columns: {
    recognised: { firstName: string[]; lastName: string[]; language: string[]; country: string[] };
    /** Columns that feed city, postal code, region, time zone and the Mailchimp data. */
    profile: string[];
    ignored: string[];
    /** Per ignored column: how many rows had a non-empty cell (a count, never a value). */
    ignoredValues: Record<string, number>;
  };
  created: {
    names: number;
    languages: number;
    countries: number;
    cities: number;
    postalCodes: number;
    regions: number;
    timezones: number;
    mailchimpMeta: number;
  };
  existingContactsFilled: number;
  refused: { languages: number; countries: number; timezones: number };
}

/** Report shape for a `mailchimp_unsubscribe_sync` job (packages/core/src/import/jobs.ts). */
export interface UnsubscribeSyncReport {
  filesProcessed: number;
  totalRows: number;
  byStatus: { unsubscribed: number; cleaned: number; other: number };
  suppressionsAdded: { unsubscribed: number; hard_bounce: number };
  alreadySuppressed: number;
  contactsDeleted: number;
  rowErrors: number;
}

export interface ImportJob {
  id: string;
  kind: ImportJobKind;
  dryRun: boolean;
  status: ImportJobStatus;
  listId: string | null;
  createdAt: number;
  updatedAt: number;
  startedAt: number | null;
  completedAt: number | null;
  totalChunks: number;
  processedChunks: number;
  totalRows: number;
  report: ImportReport | UnsubscribeSyncReport;
  error: string | null;
}

export interface ImportJobListResponse {
  items: ImportJob[];
}

export interface AddImportJobFileResponse {
  chunksAdded: number;
  rowsAdded: number;
}

export interface RunImportJobBatchResponse {
  job: ImportJob;
  done: boolean;
  chunksProcessedThisCall: number;
}

// --- DMARC-overgang (P8, docs/decisions.md "DMARC-overgang 28/9") ---

export type DmarcDisposition = 'none' | 'quarantine' | 'reject';
export type DmarcAuthResult = 'pass' | 'fail';

export interface DmarcReport {
  id: string;
  orgName: string;
  reportId: string;
  email: string | null;
  dateBegin: number;
  dateEnd: number;
  policyDomain: string;
  policyP: DmarcDisposition;
  policySp: DmarcDisposition | null;
  policyPct: number | null;
  sourceFileName: string;
  uploadedBy: string;
  uploadedAt: number;
}

/** DMARC "source IPs" are sending-infrastructure IPs, not contact data — never an email/name. */
export interface DmarcRecord {
  id: string;
  sourceIp: string;
  count: number;
  disposition: DmarcDisposition;
  dkimResult: DmarcAuthResult;
  spfResult: DmarcAuthResult;
  headerFrom: string;
  dkimDomain: string | null;
  dkimSelector: string | null;
  spfDomain: string | null;
}

export interface DmarcReportDetail extends DmarcReport {
  records: DmarcRecord[];
}

export interface DmarcReportListResponse {
  items: DmarcReport[];
}

export type KnownSenderMatchType = 'dkim_domain' | 'ip_cidr';

export interface KnownSender {
  id: string;
  label: string;
  matchType: KnownSenderMatchType;
  matchValue: string;
  createdAt: number;
}

export interface KnownSenderListResponse {
  items: KnownSender[];
}

export interface DmarcSummaryResponse {
  days: number;
  totalCount: number;
  passRate: number | null;
  byDay: { day: string; total: number; passRate: number | null }[];
  bySender: { label: string; count: number; known: boolean }[];
}

export type ChecklistItemKind = 'auto' | 'manual';

export interface ChecklistItem {
  key: string;
  kind: ChecklistItemKind;
  checked: boolean;
  checkedBy: string | null;
  checkedAt: number | null;
  note: string | null;
  updatedAt: number;
}

export interface ChecklistResponse {
  items: ChecklistItem[];
}

// ---- Buyers (`/api/buyers/*`, packages/core/src/db/tenant/buyers.repo.ts) ----

export type BuyerPeriod = 365 | 90 | 30;
export type BuyerType = 'new' | 'regular' | 'back' | 'won';
export type BuyerSegmentId = 'loyal' | 'repeat' | 'new' | 'one' | 'lapsedrep' | 'lapsed';
export type BuyerGapKey = 'd30' | 'd90' | 'd180' | 'd365' | 'y2' | 'y2plus';

/** `knownOrders` < `totalOrders`: the discount of the rest is not fetched yet (never "no code"). */
export interface BuyerCouponSummary {
  totalOrders: number;
  knownOrders: number;
  withCode: number;
  byCode: { code: string; orders: number }[];
}

/** Cached results carry when they were computed. `stale` = an older result while a new one is computed. */
export interface BuyersResult<T> {
  data: T;
  computedAt: number;
  stale: boolean;
}

export interface BuyersOverview {
  period: BuyerPeriod;
  country: string;
  currency: string;
  kpis: {
    paidOrders: number;
    returningOrders: number;
    returningShare: number | null;
    averageOrder: number | null;
    firstTimers: number;
    firstTimersAgain: number;
    againWithinYearShare: number | null;
  };
  months: { month: string; first: number; returning: number; partial: boolean }[];
  gap: { key: BuyerGapKey; orders: number }[];
  perCustomer: { key: string; buyers: number }[];
  sources: { source: string | null; orders: number }[];
  sourcesOther: number;
  countries: { country: string | null; orders: number }[];
  countriesOther: number;
  countryOptions: { country: string; orders: number }[];
  products: { name: string; orders: number }[];
  coupons: BuyerCouponSummary;
}

export interface BuyersWhyOption {
  value: string;
  label: string;
  kind: 'all' | 'campaign' | 'automation';
  orders: number;
}

export interface BuyersWhyRow {
  orderNumber: string;
  email: string | null;
  contactId: string | null;
  orderedAt: number;
  type: BuyerType;
  ordersBefore: number;
  daysSincePrevious: number | null;
  coupon: { known: boolean; codes: string[] };
}

export interface BuyersWhy {
  scope: { value: string; label: string; kind: 'all' | 'campaign' | 'automation' };
  options: BuyersWhyOption[];
  total: number;
  counts: Record<BuyerType, number>;
  coupons: BuyerCouponSummary;
  /** Orders left out because the buyer objected to profiling. */
  excluded: number;
  /** The buyers of one campaign or automation (max 200, newest first); null for "all orders". */
  rows: BuyersWhyRow[] | null;
  rowsTruncated: boolean;
  computedAt: number | null;
  stale: boolean;
}

export interface BuyersGroups {
  total: number;
  segments: { id: BuyerSegmentId; buyers: number }[];
  cohorts: {
    quarter: string;
    buyers: number;
    within90: number | null;
    within180: number | null;
    within365: number | null;
  }[];
}

export type ApiErrorBody = {
  error: string;
  message?: string;
  errors?: unknown[];
  /** `POST /campaigns/approve-group` 409: the languages that held the group back. */
  failures?: GroupApprovalFailure[];
};

/** One language that kept `POST /campaigns/approve-group` from approving anything. */
export interface GroupApprovalFailure {
  campaignId: string;
  /** e.g. `test_send_missing`, `campaign_changed`, `schedule_passed`. */
  error: string;
  /** Plain words for the admin. */
  message: string;
}

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody;
  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error);
    this.status = status;
    this.body = body;
  }
}

/** `GET /api/back-in-stock`: the "Back in stock" service mail (admin screen). */
export interface BackInStockSettings {
  enabled: boolean;
  subject: string | null;
  bodyText: string | null;
  updatedAt: number;
}
export interface BackInStockProductCount {
  productId: string;
  variationId: string;
  productName: string;
  pending: number;
}
export interface BackInStockStatus {
  settings: BackInStockSettings;
  pending: BackInStockProductCount[];
  pendingTotal: number;
}
export interface BackInStockPreview {
  language: string;
  subject: string;
  html: string;
  text: string;
}

/** `/api/landing-pages` (K5 part 2, migration 0067): hosted landing pages at `/p/<slug>`. */
export type LandingStatus = 'draft' | 'live' | 'off';
export type LandingLang = 'da' | 'de' | 'fr' | 'it' | 'nl';
export interface LocalizedText {
  default: string;
  da?: string;
  de?: string;
  fr?: string;
  it?: string;
  nl?: string;
}
export type LandingBlock =
  | { type: 'heading'; id: string; text: LocalizedText; level: 1 | 2 }
  | { type: 'text'; id: string; text: LocalizedText }
  | { type: 'image'; id: string; url: string; alt: LocalizedText }
  | { type: 'form'; id: string; buttonText: LocalizedText; successText: LocalizedText }
  | { type: 'button'; id: string; label: LocalizedText; url: string }
  | { type: 'coupon'; id: string; code: string; label: LocalizedText };
export interface LandingConfig {
  blocks: LandingBlock[];
  design: {
    backgroundColor: string;
    textColor: string;
    buttonColor: string;
    align: 'left' | 'center';
    width: 'narrow' | 'medium' | 'wide';
    font: 'sans' | 'serif';
  };
  legal: { imprintUrl: string | null; privacyUrl: string | null };
  listId: string | null;
}
export interface LandingPageInput {
  slug: string;
  title: string;
  indexable: boolean;
  countViews: boolean;
  formId: string | null;
  config: LandingConfig;
}
export interface LandingPage extends LandingPageInput {
  id: string;
  status: LandingStatus;
  createdAt: number;
  updatedAt: number;
  /** `null` when the shop has no news domain yet. */
  url: string | null;
}
export interface LandingPagesResponse {
  pages: LandingPage[];
  stats: { pageId: string; views: number; signups: number }[];
  days: number;
  maxLive: number;
}

/** `POST /api/contact-preview`: one draft rendered as one contact. Holds no e-mail address. */
export interface ContactPreview {
  found: true;
  contact: {
    firstName: string;
    lastName: string;
    language: string | null;
    country: string | null;
    suppressed: boolean;
  };
  subject: string;
  html: string;
  rules: { rule: string; shown: boolean }[];
}

/** One campaign or flow over the chosen days (K6 dashboards; from the daily rollup). */
export interface DashboardItem {
  kind: 'campaign' | 'flow';
  key: string;
  name: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
  orders: number;
  revenue: number;
}

export interface DashboardDay {
  day: string;
  sent: number;
  opened: number;
  clicked: number;
  orders: number;
  revenue: number;
}

export interface DashboardResponse {
  from: string;
  to: string;
  currency: string;
  daysRolled: number;
  daysInRange: number;
  rolledThrough: string | null;
  lastRolledAt: number | null;
  daysNotFinal: number;
  /** Finished days the rollup will still do by itself (about one a minute). */
  daysCatchingUp: number;
  items: DashboardItem[];
  daily: DashboardDay[];
  totals: Omit<DashboardItem, 'kind' | 'key' | 'name'>;
}

export interface DashboardRollupResponse {
  days: string[];
  queries: number;
  moreDue: boolean;
}

/** `GET /api/auth/passkeys` — one of the caller's own passkeys (never the key itself). */
export interface PasskeySummary {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
  /** Synced by the provider (e.g. iCloud Keychain, Google Password Manager). */
  backedUp: boolean;
}
